import React, { useState } from "react";

import { channelLabel, formatDay, statusMeta } from "../campaignUtils";

// Two series only: submitted (blue) and needs attention (red, also marked "!").
// Both validated for colour-blind separation against the white card surface.
export const ATTENTION = ["failed", "unknown", "skipped"];
export const percent = (part, whole) => whole ? Math.round((part / whole) * 100) : 0;


const H = 220;
const PAD = { top: 12, right: 8, bottom: 26, left: 40 };
const niceMax = value => {
  if (value <= 5) return 5;
  const step = Math.pow(10, Math.floor(Math.log10(value)));
  return Math.ceil(value / step) * step;
};
// Bar with 4px rounded top, square at the baseline.
const bar = (x, y, w, h, r = 4) => {
  if (h <= 0) return "";
  const rr = Math.min(r, h, w / 2);
  return `M${x},${y + h}V${y + rr}Q${x},${y} ${x + rr},${y}H${x + w - rr}Q${x + w},${y} ${x + w},${y + rr}V${y + h}Z`;
};

export const Legend = () => (
  <ul className="cs-chart-legend" aria-hidden="true">
    <li><span className="cs-chart-key cs-chart-key--ok" />Submitted</li>
    <li><span className="cs-chart-key cs-chart-key--bad" />! Needs attention</li>
  </ul>
);

export const DailyChart = ({ daily }) => {
  const [active, setActive] = useState(null);
  // Phones get two weeks so bars and labels stay readable.
  const narrow = typeof window !== "undefined" && window.innerWidth < 640;
  const span = narrow ? 14 : 30;
  const W = narrow ? 400 : 960;
  const rows = [...daily].sort((a, b) => a.date.localeCompare(b.date)).slice(-span);
  const max = niceMax(Math.max(1, ...rows.map(row => Number(row.attempted) || 0)));
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const slot = plotW / Math.max(rows.length, 1);
  const barW = Math.max(4, Math.min(28, slot * 0.6));
  const y = value => PAD.top + plotH - (value / max) * plotH;
  const labelEvery = Math.ceil(rows.length / 7);
  const hovered = active !== null ? rows[active] : null;
  const totalSubmitted = rows.reduce((sum, row) => sum + Number(row.submitted || 0), 0);
  const totalAttempted = rows.reduce((sum, row) => sum + Number(row.attempted || 0), 0);

  return (
    <div className="cs-chart" onMouseLeave={() => setActive(null)}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`Daily sends, last ${rows.length} days: ${totalSubmitted} of ${totalAttempted} submitted.`}
      >
        {[0, max / 2, max].map(tick => (
          <g key={tick}>
            <line x1={PAD.left} x2={W - PAD.right} y1={y(tick)} y2={y(tick)} className={tick ? "cs-chart-grid" : "cs-chart-base"} />
            <text x={PAD.left - 8} y={y(tick) + 4} textAnchor="end" className="cs-chart-axis">{Math.round(tick).toLocaleString()}</text>
          </g>
        ))}
        {rows.map((row, index) => {
          const x = PAD.left + slot * index + (slot - barW) / 2;
          const ok = Number(row.submitted) || 0;
          const bad = Number(row.failed) || 0;
          const okH = (ok / max) * plotH;
          const badH = (bad / max) * plotH;
          const okTop = PAD.top + plotH - okH;
          const gap = okH > 0 && badH > 0 ? 2 : 0;
          return (
            <g key={row.date} className={active !== null && active !== index ? "is-dim" : ""}>
              {ok > 0 && <path d={bar(x, okTop, barW, okH, badH > 0 ? 0 : 4)} className="cs-chart-ok" />}
              {bad > 0 && <path d={bar(x, okTop - gap - badH, barW, badH)} className="cs-chart-bad" />}
              {index % labelEvery === 0 && (
                <text x={x + barW / 2} y={H - 8} textAnchor="middle" className="cs-chart-axis">{formatDay(row.date).replace(/^\w+ /, "")}</text>
              )}
              {/* Hit target is the whole column, wider than the bar. */}
              <rect x={PAD.left + slot * index} y={PAD.top} width={slot} height={plotH} fill="transparent" onMouseEnter={() => setActive(index)} />
            </g>
          );
        })}
      </svg>
      {hovered && (
        <div className="cs-chart-tip" style={{ left: `${((PAD.left + slot * (active + 0.5)) / W) * 100}%` }}>
          <strong>{formatDay(hovered.date)}</strong>
          <span>Attempted <b>{hovered.attempted}</b></span>
          <span><i className="cs-chart-key cs-chart-key--ok" />Submitted <b>{hovered.submitted}</b></span>
          <span><i className="cs-chart-key cs-chart-key--bad" />Needs attention <b>{hovered.failed}</b></span>
        </div>
      )}
    </div>
  );
};

// Per-channel split of every send log: one horizontal bar per channel.
export const ChannelBreakdown = ({ summary }) => {
  const channels = ["sms", "push", "email"].map(channel => {
    const rows = (summary || []).filter(row => row.channel === channel);
    const sum = statuses => rows.filter(row => !statuses || statuses.includes(row.status)).reduce((s, row) => s + Number(row.total || 0), 0);
    return { channel, attempted: sum(), submitted: sum(["submitted"]), attention: sum(ATTENTION) };
  });
  const max = Math.max(1, ...channels.map(row => row.attempted));
  return (
    <ul className="cs-hbars">
      {channels.map(row => (
        <li key={row.channel}>
          <div className="cs-hbars__label">
            <strong>{channelLabel(row.channel)}</strong>
            <span>{row.attempted.toLocaleString()} sent · {percent(row.submitted, row.attempted)}% submitted</span>
          </div>
          <div className="cs-hbars__track" role="img" aria-label={`${channelLabel(row.channel)}: ${row.submitted} submitted, ${row.attention} need attention, of ${row.attempted}`}>
            {row.submitted > 0 && <span className="cs-chart-ok" style={{ width: `${(row.submitted / max) * 100}%` }} />}
            {row.attention > 0 && <span className="cs-chart-bad" style={{ width: `${(row.attention / max) * 100}%` }} />}
          </div>
        </li>
      ))}
    </ul>
  );
};

// Every status the backend reports, largest first, with glyph + label.
export const StatusBreakdown = ({ summary }) => {
  const totals = {};
  (summary || []).forEach(row => { totals[row.status] = (totals[row.status] || 0) + Number(row.total || 0); });
  const rows = Object.keys(totals).map(status => ({ status, total: totals[status] })).sort((a, b) => b.total - a.total);
  const all = rows.reduce((sum, row) => sum + row.total, 0);
  if (rows.length === 0) return <p className="cs-muted">No send results yet.</p>;
  return (
    <ul className="cs-status-rows">
      {rows.map(row => {
        const meta = statusMeta(row.status);
        return (
          <li key={row.status}>
            <span className={`cs-pill cs-pill--${meta.tone}`}><span aria-hidden="true" className="cs-pill__glyph">{meta.glyph}</span>{meta.label}</span>
            <span className="cs-status-rows__num">{row.total.toLocaleString()}</span>
            <span className="cs-muted">{percent(row.total, all)}%</span>
          </li>
        );
      })}
    </ul>
  );
};
